import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  type Observable,
  Subject,
  catchError,
  forkJoin,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';

import {
  DRIVE_CONFIG,
  DRIVE_DATA_SOURCE,
  NON_CREATABLE_DIRECTORY_KIND_NAMES,
  SECTION_DIRECTORY_KIND_NAME,
  isListableKind,
  type DirectoryKind,
  type DirectoryNode,
  type DriveItem,
  type DriveSort,
  type Page,
} from '@mtm/drive/core';

/** Payload for creating a section or sub-section directory. */
export interface CreateSubsectionInput {
  /** Display name for the new directory (usually a blueprint node name). */
  name: string;
  /** Optional back-reference to the originating blueprint node. */
  blueprintNodeUuid?: string;
}

/** One entry of the reorder batch: a child directory and its new 0-based position. */
export interface ReorderChildInput {
  directoryUuid: string;
  displayOrder: number;
}

/** A successful `appendElementProperties` write: the directory and the properties merged in. */
export interface ElementPropertiesChange {
  directoryUuid: string;
  props: Record<string, unknown>;
}

/** Envelope returned by the drive endpoints this service calls directly over HTTP. */
interface DriveApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

/** The subset of the raw directory payload this service reads. */
interface DirectoryPayload {
  elementProperties?: Record<string, unknown> | null;
}

// A project rarely has more than a handful of sections/sub-sections.
const STRUCTURE_PAGE_SIZE = 200;

/** Seeded name of the SUB-SECTION directory kind (id 7); `@mtm/drive` exports only the SECTION one. */
const SUBSECTION_DIRECTORY_KIND_NAME = 'SUB-SECTION';

/**
 * Reads and mutates a drive-mode project's section → sub-section skeleton through
 * the generic {@link DRIVE_DATA_SOURCE} port. The backend no longer creates section
 * directories at project creation, so a section is created on demand (see
 * {@link createSection}) when its first sub-section is added.
 *
 * Directory kind ids are never hardcoded: the kind to create under a parent is resolved
 * from the parent kind's `allowed_child_kinds` setting against the live kind catalog,
 * like the drive explorer's add-menu (minus non-creatable kinds). A parent may allow
 * several kinds (PROJECT allows SECTION, SHARE_LINK and OPTION), so each create names
 * the kind it wants, and never takes whichever allowed kind sorts first: a SHARE_LINK
 * is non-listable, so a section created as one is saved but missing from the
 * `listable=true` listings this service reads — it vanished on reload.
 */
@Injectable({ providedIn: 'root' })
export class DriveProjectStructureService {
  private readonly dataSource = inject(DRIVE_DATA_SOURCE);
  private readonly http = inject(HttpClient);
  private readonly directoriesBase = `${inject(DRIVE_CONFIG).apiUrl}/api/v1/drive/directories`;
  private kinds$: Observable<ReadonlyArray<DirectoryKind>> | null = null;

  private readonly elementPropertiesChanged: Subject<ElementPropertiesChange> = new Subject<ElementPropertiesChange>();
  /** Fires after every successful {@link appendElementProperties} write. */
  readonly elementPropertiesChanged$: Observable<ElementPropertiesChange> =
    this.elementPropertiesChanged.asObservable();

  /** Sections of a project — the directory children of its root, in persisted display order. */
  listSections(projectRootUuid: string): Observable<DirectoryNode[]> {
    return this.listChildDirectories(projectRootUuid, 'DEFAULT');
  }

  /**
   * Sub-sections of a section — its directory children, in the persisted manual
   * order. Uses the `DEFAULT` sort so no `sort` query parameter is sent and the
   * `displayOrder` written by {@link reorderSubsections} is honoured.
   */
  listSubsections(sectionUuid: string): Observable<DirectoryNode[]> {
    return this.listChildDirectories(sectionUuid, 'DEFAULT');
  }

  /** All children of a directory — directories always, files only when `includeFiles` is set. */
  listChildren(
    parentDirectoryUuid: string,
    opts?: { includeFiles?: boolean; sort?: DriveSort }
  ): Observable<DriveItem[]> {
    return this.dataSource
      .listItems(parentDirectoryUuid, {
        page: 0,
        size: STRUCTURE_PAGE_SIZE,
        sort: opts?.sort ?? 'NAME_ASC',
        filter: { kind: opts?.includeFiles ? null : 'DIRECTORY', mimePatterns: null, search: null },
      })
      .pipe(map((page: Page<DriveItem>) => [...page.content]));
  }

  /** Create a `SECTION` directory under a project root. */
  createSection(projectRootUuid: string, input: CreateSubsectionInput): Observable<DirectoryNode> {
    return this.createChildDirectory(projectRootUuid, input, SECTION_DIRECTORY_KIND_NAME);
  }

  /** Create a `SUB-SECTION` directory under a section. */
  createSubsection(sectionUuid: string, input: CreateSubsectionInput): Observable<DirectoryNode> {
    return this.createChildDirectory(sectionUuid, input, SUBSECTION_DIRECTORY_KIND_NAME);
  }

  /** Permanently delete a sub-section directory (mirrors the legacy X button). */
  deleteSubsection(subsectionUuid: string): Observable<void> {
    return this.dataSource.deleteDirectory(subsectionUuid);
  }

  /**
   * Persist the manual order of a section's sub-sections. The `DriveDataSource`
   * port does not expose this endpoint, so we call it directly — the Drive-scoped
   * `mtmAppTokenInterceptor` (registered against `environment.drive.apiUrl` in
   * `AppModule`) attaches the auth token.
   *
   * Partial batches are accepted: pass only the children whose position changed.
   * `displayOrder` becomes the default listing order across all drive endpoints.
   */
  reorderSubsections(sectionUuid: string, items: ReorderChildInput[]): Observable<void> {
    return this.reorderChildren(sectionUuid, items);
  }

  /** Persist the order of a project's sections — same endpoint and semantics as {@link reorderSubsections}. */
  reorderSections(projectRootUuid: string, items: ReorderChildInput[]): Observable<void> {
    return this.reorderChildren(projectRootUuid, items);
  }

  /**
   * A directory's raw `elementProperties` map. Read directly — the
   * `DriveDataSource` port's `getDirectory` strips `elementProperties` from
   * `DirectoryNode`, so this is the only way to see it.
   */
  getElementProperties(directoryUuid: string): Observable<Record<string, unknown> | null> {
    return this.http
      .get<DriveApiResponse<DirectoryPayload | null>>(`${this.directoriesBase}/${directoryUuid}`)
      .pipe(map((r) => r.data?.elementProperties ?? null));
  }

  /**
   * Merge `props` into a directory's `elementProperties` (drive
   * `appendElementProperties` — existing keys are overwritten, others kept).
   */
  appendElementProperties(directoryUuid: string, props: Record<string, unknown>): Observable<void> {
    return this.http
      .patch<DriveApiResponse<DirectoryPayload | null>>(
        `${this.directoriesBase}/${directoryUuid}/appendElementProperties`,
        {
          properties: props,
        }
      )
      .pipe(
        tap(() => this.elementPropertiesChanged.next({ directoryUuid, props })),
        map(() => undefined)
      );
  }

  private createChildDirectory(
    parentUuid: string,
    input: CreateSubsectionInput,
    kindName: string
  ): Observable<DirectoryNode> {
    return this.resolveChildKindId(parentUuid, kindName).pipe(
      switchMap((directoryKindId) =>
        this.dataSource.createDirectory({
          parentDirectoryUuid: parentUuid,
          directoryName: input.name,
          directoryDisplayName: input.name,
          directoryKindId,
          elementProperties: input.blueprintNodeUuid ? { blueprintNodeUuid: input.blueprintNodeUuid } : null,
        })
      )
    );
  }

  private reorderChildren(parentUuid: string, items: ReorderChildInput[]): Observable<void> {
    return this.http
      .patch<DriveApiResponse<null>>(`${this.directoriesBase}/${parentUuid}/children/reorder`, { items })
      .pipe(map(() => undefined));
  }

  private listChildDirectories(parentUuid: string, sort?: DriveSort): Observable<DirectoryNode[]> {
    return this.listChildren(parentUuid, { sort }).pipe(
      map((items: DriveItem[]) => items.filter((item): item is DirectoryNode => item.itemKind === 'DIRECTORY'))
    );
  }

  /**
   * Resolve the directory kind to create under `parentUuid` from the parent kind's
   * `allowed_child_kinds` and the live kind catalog: `kindName` when the parent allows
   * it, else the first allowed kind that is creatable and listable. Returns null when
   * none qualifies (the backend then applies its own default).
   */
  private resolveChildKindId(parentUuid: string, kindName: string): Observable<number | null> {
    return forkJoin({
      parent: this.dataSource.getDirectory(parentUuid),
      kinds: this.getKinds(),
    }).pipe(
      map(({ parent, kinds }) => {
        const creatable = this.allowedChildKinds(parent, kinds).filter(
          (k) => !NON_CREATABLE_DIRECTORY_KIND_NAMES.has(k.directoryKindName)
        );
        const kind = creatable.find((k) => k.directoryKindName === kindName) ?? creatable.find(isListableKind);
        return kind?.directoryKindId ?? null;
      })
    );
  }

  /**
   * Port of the explorer's allowed-child-kind resolution: reads the parent kind's
   * `settings.allowed_child_kinds` (a list of kind names, or `*`), falling back to all
   * root-creatable kinds when unspecified.
   */
  private allowedChildKinds(dir: DirectoryNode, allKinds: ReadonlyArray<DirectoryKind>): ReadonlyArray<DirectoryKind> {
    const allowed: unknown = dir.directoryKind?.settings?.['allowed_child_kinds'];
    const rootCreatable = (k: DirectoryKind): boolean => k.settings?.['root_creatable'] !== false;

    if (!Array.isArray(allowed)) return allKinds.filter(rootCreatable);
    if (allowed.length === 0) return [];
    if (allowed.includes('*')) return allKinds.filter(rootCreatable);

    const codes = allowed.filter((v): v is string => typeof v === 'string');
    return allKinds.filter((k) => codes.includes(k.directoryKindName));
  }

  private getKinds(): Observable<ReadonlyArray<DirectoryKind>> {
    if (!this.kinds$) {
      this.kinds$ = this.dataSource.listKinds().pipe(
        // `listKinds()` is a LIVE stream that never completes — it re-emits on a
        // background revalidation or a scope refetch. `resolveChildKindId` feeds it
        // into a `forkJoin`, which only emits once every source completes, so take
        // the first snapshot (what the library's own node mappers do internally).
        // Without this the create path hangs forever and never fires its POST.
        take(1),
        // Drop the cache on failure so a transient listKinds() error isn't
        // replayed forever (mirrors drive-fe's DriveDirectoryService).
        catchError((err) => {
          this.kinds$ = null;
          return throwError(() => err);
        }),
        shareReplay(1)
      );
    }
    return this.kinds$;
  }
}
